import { BackOfficeUser } from "../domain/user.interface";
import {
  GetUsersForProgram,
  UsersPayload,
  UsersService,
} from "../services/users.service";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useUsers = (
  {
    sortField,
    sortOrder,
    pageNumber,
    pageSize,
    search,
    status,
    roleId,
    programId,
    orgUnitId,
  }: UsersPayload,
  enabled = true,
) => {
  const {
    data: users,
    isLoading: isLoadingUsers,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: [
      "coyn-users",
      sortField,
      sortOrder,
      pageNumber,
      pageSize,
      search,
      status,
      roleId,
      programId,
      orgUnitId,
    ],
    queryFn: () =>
      UsersService.findAll({
        sortField,
        sortOrder,
        pageNumber,
        pageSize,
        search,
        status,
        roleId,
        programId,
        orgUnitId,
      }),
    enabled,
  });

  return {
    users,
    isLoadingUsers,
    refetchUsers,
  };
};

export const useUserByEmail = ({ email }: { email: string }) => {
  const {
    data: user,
    isLoading: isLoadingUser,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ["coyn-user-by-email"],
    queryFn: () => UsersService.findByEmail(email),
  });

  return {
    user,
    isLoadingUser,
    refetchUser,
  };
};

export const useGetCmCoordinator = (
  { programId, orgUnitId }: GetUsersForProgram,
  enabled = true,
) => {
  const {
    data: cmCoordinatorUsers,
    isLoading: isLoadingCmCoordinatorUsers,
    refetch: refetchCmCoordinatorUsers,
    isFetching: isFetchingCmCoordinatorUsers,
  } = useQuery({
    queryKey: ["coyn-cm-coordinator-users", programId, orgUnitId],
    queryFn: () =>
      UsersService.getCmCoordinator({
        programId,
        orgUnitId,
      }),
    enabled,
  });

  return {
    cmCoordinatorUsers,
    isLoadingCmCoordinatorUsers,
    refetchCmCoordinatorUsers,
    isFetchingCmCoordinatorUsers,
  };
};

export const useGetAdditionalStaff = (
  { programId, orgUnitId }: GetUsersForProgram,
  enabled = true,
) => {
  const {
    data: additionalStaffUsers,
    isLoading: isLoadingAdditionalStaffUsers,
    refetch: refetchAdditionalStaffUsers,
    isFetching: isFetchingAdditionalStaffUsers,
  } = useQuery({
    queryKey: ["coyn-additional-staff-users", programId, orgUnitId],
    queryFn: () =>
      UsersService.getAdditionalStaff({
        programId,
        orgUnitId,
      }),
    enabled,
  });

  return {
    additionalStaffUsers,
    isLoadingAdditionalStaffUsers,
    refetchAdditionalStaffUsers,
    isFetchingAdditionalStaffUsers,
  };
};

export const useProgramsByUser = ({
  userId,
  sortType,
}: {
  userId: string;
  sortType: string;
}) => {
  const {
    data: programs,
    isLoading: isLoadingPrograms,
    isFetching: isFetchingPrograms,
    refetch: refetchPrograms,
  } = useQuery({
    queryKey: ["coyn-programs", userId],
    queryFn: () =>
      UsersService.findProgramsByUser({
        id: userId,
        sortType,
      }),
  });

  return {
    programs,
    isLoadingPrograms,
    isFetchingPrograms,
    refetchPrograms,
  };
};

export const useUserCreateOne = () => {
  return useMutation({
    mutationFn: (data: BackOfficeUser) => UsersService.createOne(data),
  });
};

export const useUserUpdateOne = () => {
  return useMutation({
    mutationFn: async ({
      user,
      userId,
    }: {
      user: BackOfficeUser;
      userId: string;
    }) => {
      await UsersService.updateOne({
        user,
        userId,
      });
    },
  });
};

export const useUserDeleteOne = () => {
  return useMutation({
    mutationFn: (data: { userId: string }) => UsersService.deleteOne(data),
  });
};

export const useUserResendInvitation = () => {
  return useMutation({
    mutationFn: (email: string) => UsersService.resendInvitation(email),
  });
};

export const useUserById = (userId: string, enabled = true) => {
  const {
    data: user,
    isLoading: isLoadingUser,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ["coyn-user-by-id", userId],
    queryFn: () => UsersService.findById(userId),
    enabled: !!userId && enabled,
  });

  return {
    user,
    isLoadingUser,
    refetchUser,
  };
};
